import { useCallback, useState } from 'react';
import { toUserMessage } from '@platform/api-client';
import type { JSONContent } from '@tiptap/react';
import { Button } from '@platform/ui';
import { PostEditor } from '@/features/board';
import { INQUIRY_CATEGORIES } from '../constants.ts';
import type { CreateInquiryInput, Inquiry, InquiryCategory } from '../inquiry.type.ts';

interface InquiryFormProps {
  /** 수정 시 기존 문의. 생성이면 undefined */
  initial?: Inquiry;
  onSubmit: (input: CreateInquiryInput) => Promise<void>;
  submitting: boolean;
  onCancel: () => void;
  onError?: (message: string) => void;
}

const inputBase =
  'w-full rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-dark-card px-4 py-3 text-[15px] text-neutral-950 dark:text-neutral-100 outline-none transition-colors focus:border-neutral-950';

const isEmptyDoc = (doc?: JSONContent): boolean => {
  const content = doc?.content;
  return !content || content.length === 0;
};

/**
 * 문의 작성/수정 공통 폼.
 * 분류(category) + 제목(title) + 내용(body, Tiptap) 의 단순 구조.
 * 본문 비어있음/제목 누락은 클라이언트에서 막고, 서버가 최종 검증.
 */
export function InquiryForm({
  initial,
  onSubmit,
  submitting,
  onCancel,
  onError,
}: InquiryFormProps) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [category, setCategory] = useState<InquiryCategory | ''>(
    initial?.category ?? '',
  );
  const [body, setBody] = useState<JSONContent | undefined>(initial?.body);
  const [errors, setErrors] = useState<{
    title?: string;
    category?: string;
    body?: string;
  }>({});

  const handleSubmit = useCallback(async () => {
    const next: typeof errors = {};
    if (!title.trim()) next.title = '제목을 입력해주세요.';
    if (!category) next.category = '분류를 선택해주세요.';
    if (isEmptyDoc(body)) next.body = '내용을 입력해주세요.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    try {
      await onSubmit({
        title: title.trim(),
        category: category as InquiryCategory,
        body: body!,
      });
    } catch (err) {
      onError?.(
        toUserMessage(err, '저장 중 오류가 발생했습니다.'),
      );
    }
  }, [title, category, body, onSubmit, onError]);

  return (
    <div className="space-y-6">
      {/* 분류 */}
      <div>
        <label
          htmlFor="category"
          className="mb-2 block text-[13px] font-semibold text-neutral-950 dark:text-neutral-100"
        >
          분류 <span className="text-[#FA0F00]">*</span>
        </label>
        <select
          id="category"
          value={category}
          onChange={(e) => setCategory(e.target.value as InquiryCategory)}
          className={inputBase}
        >
          <option value="" disabled>
            선택하세요
          </option>
          {INQUIRY_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        {errors.category && (
          <p className="mt-1.5 text-[12px] text-[#FA0F00]">{errors.category}</p>
        )}
      </div>

      {/* 제목 */}
      <div>
        <label
          htmlFor="title"
          className="mb-2 block text-[13px] font-semibold text-neutral-950 dark:text-neutral-100"
        >
          제목 <span className="text-[#FA0F00]">*</span>
        </label>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="제목을 입력하세요"
          maxLength={200}
          className={inputBase}
        />
        {errors.title && (
          <p className="mt-1.5 text-[12px] text-[#FA0F00]">{errors.title}</p>
        )}
      </div>

      {/* 내용 */}
      <div>
        <label className="mb-2 block text-[13px] font-semibold text-neutral-950 dark:text-neutral-100">
          내용 <span className="text-[#FA0F00]">*</span>
        </label>
        <PostEditor
          initialContent={initial?.body}
          onChange={setBody}
          onError={onError}
        />
        {errors.body && (
          <p className="mt-1.5 text-[12px] text-[#FA0F00]">{errors.body}</p>
        )}
      </div>

      {/* 액션 */}
      <div className="flex justify-end gap-2 border-t border-black/10 dark:border-white/10 pt-5">
        <Button variant="secondary" onClick={onCancel} disabled={submitting}>
          취소
        </Button>
        <Button onClick={handleSubmit} loading={submitting}>
          {initial ? '수정 완료' : '문의 등록'}
        </Button>
      </div>
    </div>
  );
}
